/**
 * Public Page Tab - Schedule Public Page Configuration
 *
 * Provides configuration interface for public schedule display:
 * - Page title customization
 * - Teacher name format (privacy settings)
 * - External URL for back button
 * - Embed code generator with copy functionality
 * - Preview link
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Button,
  Alert,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import {
  useGetSchedulePublicConfigQuery,
  useUpdateSchedulePublicConfigMutation,
} from '../../../api/schedulingApi';
import type { TeacherNameFormat } from '../../../types/scheduling';

interface PublicPageTabProps {
  scheduleId: string;
  studioId: string;
  studioSlug: string;
}

const PublicPageTab: React.FC<PublicPageTabProps> = ({
  scheduleId,
  studioId,
  studioSlug,
}) => {
  // API hooks
  const {
    data: config,
    isLoading,
    error,
  } = useGetSchedulePublicConfigQuery(
    { scheduleId, studioId },
    { skip: !scheduleId || !studioId }
  );

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateSchedulePublicConfigMutation();

  // Form state
  const [title, setTitle] = useState('');
  const [teacherNameFormat, setTeacherNameFormat] =
    useState<TeacherNameFormat>('first_only');
  const [externalUrl, setExternalUrl] = useState('');

  // UI state
  const [embedCodeCopied, setEmbedCodeCopied] = useState(false);
  const [previewUrlCopied, setPreviewUrlCopied] = useState(false);

  // Initialize form from config
  useEffect(() => {
    if (config) {
      setTitle(config.title || '');
      setTeacherNameFormat(config.teacher_name_format || 'first_only');
      setExternalUrl(config.external_url || '');
    }
  }, [config]);

  const handleSave = async () => {
    if (!config) return;

    try {
      await updateConfig({
        id: config.id,
        studioId,
        scheduleId,
        data: {
          title,
          teacher_name_format: teacherNameFormat,
          external_url: externalUrl,
        },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update public page config:', err);
    }
  };

  // Auto-save on change with debounce
  useEffect(() => {
    if (!config) return undefined;

    const timeoutId = setTimeout(() => {
      const hasChanges =
        title !== config.title ||
        teacherNameFormat !== config.teacher_name_format ||
        externalUrl !== config.external_url;

      if (hasChanges) {
        handleSave();
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, teacherNameFormat, externalUrl]);

  const publicUrl = `${window.location.origin}/schedule/${studioSlug}`;

  const embedCode = `<iframe
  src="${publicUrl}"
  width="100%"
  height="800"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Class Schedule"
></iframe>`;

  const copyToClipboard = (text: string, type: 'embed' | 'preview') => {
    navigator.clipboard.writeText(text);
    if (type === 'embed') {
      setEmbedCodeCopied(true);
      setTimeout(() => setEmbedCodeCopied(false), 2000);
    } else {
      setPreviewUrlCopied(true);
      setTimeout(() => setPreviewUrlCopied(false), 2000);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load public page configuration. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Configuration Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Public Page Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Customize how your schedule appears on the public page. Changes are
          saved automatically.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
          {/* Page Title */}
          <TextField
            label="Page Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            helperText="The title displayed at the top of your public schedule page"
            disabled={isUpdating}
          />

          {/* Teacher Name Format */}
          <FormControl fullWidth>
            <InputLabel>Teacher Name Format</InputLabel>
            <Select
              value={teacherNameFormat}
              onChange={e =>
                setTeacherNameFormat(e.target.value as TeacherNameFormat)
              }
              label="Teacher Name Format"
              disabled={isUpdating}
            >
              <MenuItem value="full">First Name Last Name</MenuItem>
              <MenuItem value="first_last_initial">
                First Name Last Initial
              </MenuItem>
              <MenuItem value="first_only">First Name Only</MenuItem>
              <MenuItem value="initials">First Initial Last Initial</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Control how teacher names are displayed for privacy
            </Typography>
          </FormControl>

          {/* External URL */}
          <TextField
            label="Back Button URL (Optional)"
            value={externalUrl}
            onChange={e => setExternalUrl(e.target.value)}
            fullWidth
            placeholder="https://yourstudio.com"
            helperText="Add a link back to your main website. Leave blank to hide the back button."
            disabled={isUpdating}
          />
        </Box>
      </Paper>

      {/* Preview Link Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Preview Link
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Share this link to let people view your class schedule
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            backgroundColor: 'grey.100',
            p: 2,
            borderRadius: 1,
          }}
        >
          <Link
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ flexGrow: 1, wordBreak: 'break-all' }}
          >
            {publicUrl}
          </Link>
          <Tooltip title="Open in new tab">
            <IconButton
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              color="primary"
            >
              <OpenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={previewUrlCopied ? 'Copied!' : 'Copy link'}>
            <IconButton
              onClick={() => copyToClipboard(publicUrl, 'preview')}
              size="small"
              color={previewUrlCopied ? 'success' : 'primary'}
            >
              {previewUrlCopied ? <CheckIcon /> : <CopyIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Embed Code Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Embed Code
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Copy this code to embed your schedule on your website
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline
            rows={8}
            value={embedCode}
            fullWidth
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                backgroundColor: 'grey.100',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          >
            <Tooltip title={embedCodeCopied ? 'Copied!' : 'Copy code'}>
              <Button
                variant="contained"
                size="small"
                startIcon={embedCodeCopied ? <CheckIcon /> : <CopyIcon />}
                onClick={() => copyToClipboard(embedCode, 'embed')}
                color={embedCodeCopied ? 'success' : 'primary'}
              >
                {embedCodeCopied ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Only published schedules are visible on the
            public page. Make sure to publish your schedule from the Overview
            tab.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default PublicPageTab;
